import { IsArray, IsBoolean, IsEnum, IsNotEmpty, IsNumberString, IsObject, IsOptional, IsString, Matches, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { VisibilityConditionDto } from '../visibility-condition.dto';
import { CustomizationOptionDto, CustomizationOptionMappingDto } from './customization-option.dto';
import { LayerDto } from '../layer/layer.dto';
import { CustomizationUtils } from '../../utils/customization.utils';
import { CreativeUnitDimensionsDto, CreativeUnitDto } from '../creative-unit/creative-unit.dto';
import { CreativeUnitUtils } from '../../utils/creative-unit.utils';
import { AssetDto } from '../../../asset/models/asset.dto';
import { LayersUtils } from '../../utils/layers.utils';
import { cloneDeep, merge } from 'lodash';
import { LayerAudioDto } from '../layer/layer-audio.dto';
import { LayerVideoDto } from '../layer/layer-video.dto';
import { ImageActionType } from '../../../asset/models/image-action.dto';
import { QueryParams } from '../../../data-source/models';
import { PublicDataSource } from '../../../data-source/data-source.entity';
import { MergeTags } from '../../../_core/utils/utils.merge-tags';
import { Evaluate } from '../../../_core/utils/utils.evaluate';
import { LocaleCollectionDto } from '../locale.dto';
import { ObjectUtils } from '../../../_core/utils/utils.object';
import { VideoActionType } from '../../../asset/models/video-action.dto';
import { Validation } from '../../../_core/utils/utils.validation';
import { CreativeUnitPackageDto } from '../creative-unit-package.dto';
import { PublicBusiness } from '../../../business/business.entity';
import { MergePropertiesUtils } from '../../utils/merge-properties.utils';
import { AudioActionType } from '../../../asset/models/audio-action.dto';
import { String } from '../../../_core/utils/utils.string';
import { MergeTagDto } from "../merge-tag/merge-tag.dto";
import { CustomizationOptionUtils } from "../../utils/customization-option.utils";
import { ProjectCartItemGroupDto } from '../../../cart/models/project-cart-item-group.dto';

export interface CustomizationValueChange {
	key: string;
	value: any;
	dirty?: boolean;
}

export interface CustomizationValueChange {
	key: string;
	value: any;
	dirty?: boolean;
}

export enum CustomizationType {
	TEXT = 'text',
	SELECT = 'select',
	TOGGLE = 'toggle',
	IMAGE_PICKER = 'image-picker',
	VIDEO_PICKER = 'video-picker',
	AUDIO_PICKER = 'audio-picker',
	COLOR_PICKER = 'color-picker',
	DIMENSIONS = 'dimensions',
	SLIDER = 'slider',
	MESSAGE = 'message',
	GROUP = 'group'
}

export const customizationIconsByType = {
	[CustomizationType.TEXT]: 'text_snippet',
	[CustomizationType.SELECT]: 'list',
	[CustomizationType.TOGGLE]: 'toggle_on',
	[CustomizationType.IMAGE_PICKER]: 'photo',
	[CustomizationType.VIDEO_PICKER]: 'smart_display',
	[CustomizationType.AUDIO_PICKER]: 'music_note',
	[CustomizationType.COLOR_PICKER]: 'palette',
	[CustomizationType.DIMENSIONS]: 'aspect_ratio',
	[CustomizationType.SLIDER]: 'tune',
	[CustomizationType.MESSAGE]: 'sticky_note_2',
	[CustomizationType.GROUP]: 'layers'
}


export type CustomizationItemDto =
	| CustomizationTextDto
	| CustomizationSelectDto
	| CustomizationToggleDto
	| CustomizationImagePickerDto
	| CustomizationVideoPickerDto
	| CustomizationAudioPickerDto
	| CustomizationColorPickerDto
	| CustomizationDimensionsDto
	| CustomizationSliderDto
	| CustomizationMessageDto
	| CustomizationGroupDto;

/**
 * An easy way to get each of our customization classes via a type string - only atomic customizations
 */
export function getCustomizationClassByType(type: string) {
	switch (type) {
		case CustomizationType.TEXT:
			return CustomizationTextDto;
		case CustomizationType.SELECT:
			return CustomizationSelectDto;
		case CustomizationType.IMAGE_PICKER:
			return CustomizationImagePickerDto;
		case CustomizationType.VIDEO_PICKER:
			return CustomizationVideoPickerDto;
		case CustomizationType.AUDIO_PICKER:
			return CustomizationAudioPickerDto;
		case CustomizationType.COLOR_PICKER:
			return CustomizationColorPickerDto;
		case CustomizationType.DIMENSIONS:
			return CustomizationDimensionsDto;
		case CustomizationType.TOGGLE:
			return CustomizationToggleDto;
		case CustomizationType.SLIDER:
			return CustomizationSliderDto;
		case CustomizationType.MESSAGE:
			return CustomizationMessageDto;
		default:
			return CustomizationDto;
	}
}

export abstract class CustomizationDto {
	@IsOptional()
	@IsString()
	id: string;

	@IsOptional()
	@IsString()
	parentId?: string; // Used when flattening out customizations to trace back to parent.

	@IsOptional()
	@IsString()
	name?: string;

	@IsOptional()
	@IsString()
	label?: string;

	@IsOptional()
	@IsString()
	hint?: string;

	@IsOptional()
	@IsString()
	placeholder?: string;

	@ValidateNested({ each: true })
	@IsOptional()
	@Type(() => VisibilityConditionDto)
	visibilityConditions?: VisibilityConditionDto | VisibilityConditionDto[];

	@IsOptional()
	@IsBoolean()
	onlyShowInEditor?: boolean;

	@IsOptional()
	setTimeOnFocus?: {
		markerSlug?: string;
		time?: string;
	};

	@IsOptional()
	layerIds?: string[];

	@IsOptional()
	defaultValue?: any;

	@IsOptional()
	@Type(() => CustomizationOptionDto)
	defaultOption?: CustomizationOptionDto;

	// Mappings is used to add extra logic to layers or variables.  Used primarily for media pickers as of now.
	@IsOptional()
	mappings?: CustomizationOptionMappingDto;

	@IsOptional()
	@IsBoolean()
	dirty?: boolean; // Has the customization been manually manipulated by the user?

	/*
	 * Dimensions: Randomize Options
	 */
	@IsOptional()
	@IsBoolean()
	showRandomize?: boolean;

	@IsOptional()
	@IsNumberString()
	randomizeMaxPixels?: string;

	@IsOptional()
	@IsNumberString()
	randomizeMinPixels?: string;

	@IsOptional()
	locale?: LocaleCollectionDto;

	@IsOptional()
	@IsArray()
	children?: CustomizationItemDto[];

	@IsOptional()
	@IsObject()
	mergeTags?: Record<string, MergeTagDto[]>;

	/**
	 * Retrieves the default value for the customization
	 */
	public static getDefaultValue(customization: CustomizationItemDto, layers?: LayerDto[]): any {
		return customization.value;
	}

	/**
	 * Retrieve the default placeholder value for the customization.
	 *
	 */
	public static getPlaceholderValue(customization: CustomizationItemDto, layers: LayerDto[]): any {
		return customization.placeholder;
	}

	/**
	 * A mapping function to take a value data and retrieve the proper data entity that the value represents.
	 * Ex. passing a string value for a select customization would go find the proper options object
	 * that has that value property, then return the full object as a result.
	 */
	public static findMatchedValue(customization: CustomizationItemDto, value: any): any {
		// Typically we would not want to adopt the value that already exists, only in some types that extend this class.
		return undefined;
	}

	/**
	 * A hook to update a layer based on the value of a customization.
	 */
	public static updateLayer(layer: LayerDto, customization: CustomizationItemDto, value: any, creativeUnit: CreativeUnitDto): LayerDto {
		return layer;
	}

	/**
	 * A hook to update multiple layers based on the value of a customization.
	 */
	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationItemDto,
		value: any,
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		return layers;
	}

	/**
	 * Synchronizes values of the ad unit back to any relevant customizations.
	 *
	 * @param {CreativeUnitWithCustomizations} creativeUnit - The ad unit to synchronize values for.
	 * @return {CreativeUnitWithCustomizations} A new `CreativeUnitWithCustomizations` object with synchronized values.
	 */
	public static applyDefaultValues2(
		customizations: CustomizationItemDto[],
		layers: LayerDto[],
		mergeTagState: { [key: string]: any },
		applyToValue?: boolean
	): CustomizationItemDto[] {
		// Iterate through the customizations in the layer.

		return CustomizationUtils.mapAllCustomizations(customizations, customization => {
			// Find the customization in the ad unit.
			const customizationClass = getCustomizationClassByType(customization.type);

			const defaultValueResult = customizationClass?.getDefaultValue(customization, layers, mergeTagState);
			const { value, dirty } = CustomizationUtils.unwrapDefaultCustomizationValue(defaultValueResult, customization.dirty);

			// Let's apply placeholder values as well.
			let placeholder = customizationClass.getPlaceholderValue(customization, layers);

			// Set the value on the customization.
			let newCustomization = {
				...customization,
				dirty,
				placeholder,
				value
			};

			return newCustomization;
		});
	}

	// NOTE: Deprecated - use applyDynamicDefaultValues instead.
	public static applyDefaultValues(
		customizations: CustomizationItemDto[],
		pkg: CreativeUnitPackageDto,
		business?: PublicBusiness,
		unit?: CreativeUnitDto,
		onlyApplyDownstreamOfId?: string,
		activeGroup?: ProjectCartItemGroupDto
	): CustomizationItemDto[] {
		let newUnit = cloneDeep(unit);
		let newPkg = cloneDeep(pkg);
		let clonedCustomizations = cloneDeep(customizations);

		// let newCustomizations = CustomizationUtils.mapAllCustomizations(customizations, customization => {
		// 	let mergeState = MergePropertiesUtils.getMergeableProperties(newPkg, business, newUnit);

		// 	// Find the customization in the ad unit.
		// 	const customizationClass = getCustomizationClassByType(customization.type);
		// 	let value = customizationClass?.getDefaultValue(customization, newUnit?.layers || newPkg.layers || [], mergeState) ?? null;

		// 	// Let's apply placeholder values as well.
		// 	let placeholder = customizationClass.getPlaceholderValue(customization, newUnit?.layers || newPkg.layers || []);

		// 	// Set the value on the customization.
		// 	let newCustomization = {
		// 		...customization,
		// 		placeholder,
		// 		value
		// 	};

		// 	// Update the unit or package so that the merge tags will be applied correctly next time.
		// 	if (unit) {
		// 		newUnit = {
		// 			...newUnit,
		// 			customizations
		// 		}
		// 	} else {
		// 		newPkg = {
		// 			...newPkg,
		// 			customizations
		// 		}
		// 	}

		// 	return newCustomization;
		// });

		// console.log('New Customizations', customizations);
		// return newCustomizations;
		// Set a flag to detect what is downstream of the customization.
		let downstream = false;

		// Iterate through the customizations in the layer.
		let flattenedCustomizations = CustomizationUtils.findAllCustomizations(customizations);

		// Iterate through the flattend customizations.
		for (let customization of flattenedCustomizations) {
			// If we are only applying downstream of a specific customization, set the flag.
			if (onlyApplyDownstreamOfId && customization.id === onlyApplyDownstreamOfId) {
				// console.log('Found it, everything else is downstream', customization.id, onlyApplyDownstreamOfId);
				downstream = true;
				continue;
			} else if (onlyApplyDownstreamOfId && !downstream) {
				// console.log('Not downstream yet', customization.id, onlyApplyDownstreamOfId);
				continue;
			}

			// Get new merge tags.  This is important to be in the loop because the merge tags can change based on the previous customizations values.
			let mergeState = MergePropertiesUtils.getMergeableProperties(newPkg, business, newUnit, activeGroup);
			// console.log('Latest Merge State', mergeState, newPkg, newUnit);

			let layers = newUnit?.layers || newPkg?.layers || [];
			let visibleLayers;
			if ([CustomizationType.VIDEO_PICKER, CustomizationType.IMAGE_PICKER, CustomizationType.TEXT, CustomizationType.AUDIO_PICKER].includes(customization.type)){
				let customizationLayers =  CreativeUnitUtils.getLayersFromCustomization(customization, layers) || [];
				visibleLayers = LayersUtils.findAllVisibleLayers(customizationLayers, mergeState);
				if (visibleLayers?.length ) {
					// If there are no visible layers, we cannot apply the default value.
					layers = visibleLayers;
				}
			}

			// Find the customization in the ad unit.
			const customizationClass = getCustomizationClassByType(customization.type);

			const defaultValueResult = customizationClass?.getDefaultValue(customization as any, layers, mergeState);
			const { value, dirty } = CustomizationUtils.unwrapDefaultCustomizationValue(defaultValueResult, customization.dirty);

			// Let's apply placeholder values as well.
			let placeholder = customizationClass.getPlaceholderValue(customization as any, layers);

			// Set the value on the customization.
			let newCustomization = {
				...customization,
				dirty,
				placeholder,
				value
			};

			// Delete the parent id from the customization.
			delete newCustomization.parentId;

			// Update the customization in the list.
			clonedCustomizations = CustomizationUtils.mapAllCustomizations(clonedCustomizations, c => {
				if (c.id === customization.id) {
					return newCustomization;
				}
				return c;
			});

			// Update the unit or package so that the merge tags will be applied correctly next time.
			if (unit) {
				newUnit = {
					...newUnit,
					customizations: clonedCustomizations
				};
			} else {
				newPkg = {
					...newPkg,
					customizations: clonedCustomizations
				};
			}
		}

		// console.log('New Customizations', clonedCustomizations);
		return clonedCustomizations;
	}

	/**
	 * Build a set of variant customizations based on the different customization values passed in.
	 */
	public static buildVariations(customizations: CustomizationSelectDto[], variantIds: string[]): CustomizationItemDto[][] {
		let combinations = [];

		function recurse(index, currentCombination) {
			if (index === customizations.length) {
				combinations.push([...currentCombination]);
				return;
			}

			const currentObject = customizations[index];

			// If the object's ID is in the provided IDs array, generate combinations for it.
			if (variantIds.includes(currentObject.id)) {
				for (let option of currentObject.options) {
					const newObject = { ...currentObject, value: option };
					recurse(index + 1, [...currentCombination, newObject]);
				}
			}
			// If not, just push it to the current combination and continue the recursion.
			else {
				recurse(index + 1, [...currentCombination, currentObject]);
			}
		}

		recurse(0, []);
		// console.log('Combinations', combinations, customizations, variantIds);
		return combinations;
	}

	public static applyStringValue(customization: CustomizationItemDto, value: string): CustomizationItemDto {
		return customization;
	}

	public static applyMappingsToLayers(
		layer: LayerDto,
		mappings: CustomizationOptionMappingDto,
		value: any,
		creativeUnit?: CreativeUnitDto
	): LayerDto {
		// console.log('Applying Mappings: Original mappings', customization.label, mappings, customization.value);

		try {
			// console.log('Adding Image Picker variables', customization.mappings?.variables, customization.value);
			let mappingsString = JSON.stringify(mappings);
			mappings = JSON.parse(MergeTags.applyMergeTagsToString(mappingsString, value));

			// Clean the object over to make sure undefined values are removed.
			mappings = ObjectUtils.cleanObject(mappings);
			// console.log('Cleaned Mappings', mappings);
		} catch (e) {
			/*console.error(e);*/
		}

		// console.log('Applying Mappings: Merged mappings', customization.label, mappings);

    let newLayer = mappings?.layers?.find(l => l.id === layer.id);

    if (newLayer) {
      // Inherit the properties of the other layer.
      const mergedLayer = merge(cloneDeep(layer), newLayer);

      // Specifically make sure visibility exists or not.
      let layerWithVisibility = { ...mergedLayer } as LayerDto;
      if (typeof newLayer.hidden === 'boolean') {
              layerWithVisibility.hidden = newLayer.hidden;
      }

      return CustomizationUtils.applyCustomizationValueToLayer(value, layerWithVisibility, creativeUnit);
    } else {
            return layer;
    }
  }

	public static getValueOrDefault(type: CustomizationType, value: any): any {
		switch (type) {
			case CustomizationType.TEXT:
				return value.value;
			case CustomizationType.SELECT:
			case CustomizationType.TOGGLE:
			case CustomizationType.SLIDER:
				return value.value;
			case CustomizationType.AUDIO_PICKER:
			case CustomizationType.VIDEO_PICKER:
			case CustomizationType.IMAGE_PICKER:
				return value.title;
			default:
				return value || value.value;
		}
	}

	public static getLabelOrDefault(customization: CustomizationItemDto): any {
		switch (customization.type) {
			case CustomizationType.SELECT:
			case CustomizationType.TOGGLE:
				return customization.value?.label || customization.defaultValue?.label;
			case CustomizationType.IMAGE_PICKER:
				return customization.value?.title || customization.defaultValue?.value;
			case CustomizationType.VIDEO_PICKER:
				return customization.value['file']?.title || customization.value['title'] || customization.defaultValue.value;
			case CustomizationType.AUDIO_PICKER:
				return customization.value.file.title || customization.defaultValue.value;
			case CustomizationType.COLOR_PICKER:
				return customization.value?.label || customization.defaultValue?.label;
			case CustomizationType.DIMENSIONS:
				// let value = customization.value?.width || customization.defaultValue
				return customization.value?.width + ' ' + customization.value.height;
			case CustomizationType.GROUP:
				return null;
			case CustomizationType.TEXT:
			default:
				return customization.label || customization.defaultValue?.label;
		}
	}
}

export class CustomizationTextDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.TEXT;

	@IsString()
	@IsOptional()
	value?: string;

	@IsString()
	@IsOptional()
	template?: 'text' | 'textarea' | 'wysiwyg' | 'wysiwyg-bubble' | 'wysiwyg-toolbar' | 'wysiwyg-simple' | 'wysiwyg-regular' | 'wysiwyg-complex';

	@IsString()
	@IsOptional()
	variableKey?: string; // If this is set, apply the content to a variable.

	@IsOptional()
	maxLength?: number;

	@IsOptional()
	@IsBoolean()
	loadDefaultText?: boolean;

	@IsOptional()
	@IsBoolean()
	enableWritingTools?: boolean;

	@IsOptional()
	@IsString()
	editorTheme?: 'bubble' | 'snow';

	@IsOptional()
	@IsObject()
	wysiwygModules?: any; // QuillModules type - using any for cross-platform compatibility

	@IsOptional()
	@IsBoolean()
	enableHtmlCodeEditor?: boolean;

	public static getDefaultValue(customization: CustomizationTextDto, layers?: LayerDto[], mergeTagState?: { [key: string]: any }) {
		if (customization.variableKey && mergeTagState?.[customization.variableKey]) {
			return { newValue: mergeTagState?.[customization.variableKey], dirty: false };
		}

		if (!layers?.length) {
			return { newValue: '', dirty: false };
		}

		let value: string;
		let dirty = customization.dirty;

		if (customization.loadDefaultText) {
			// if the customization is dirty, we want to give priority to the existing value
			if (dirty === true) {
				if (customization.value) {
					value = customization.value;
				} else if (customization.placeholder) {
					value = customization.placeholder;
					dirty = false;
				}

			} else { // if not dirty, prefer the placeholder value
				if (customization.placeholder) {
					value = customization.placeholder;
				} else if (customization.value) {
					value = customization.value;
				}
			}

			if (!value?.length) {
				value = '';
			}
		} else {
			// Use the value if it exists, otherwise, don't pull a default.
			// This is so that the fields will be blank and the placeholder will fill in.
			value = customization.value || undefined;
		}

		if (value?.length) {
			try {
				// TODO: Scripts - Need package data for script support
				value = MergeTags.applyMergeTagsToString(value, mergeTagState);
			} catch (e) {
				console.warn(`Error applying merge tags to default value for customization ${customization.label}:`, e);
			}
		}

		return { newValue: value, dirty: dirty };
	}

	public static getPlaceholderValue(customization: CustomizationTextDto, layers?: LayerDto[]) {
		const applicableLayers = CreativeUnitUtils.getLayersFromCustomization(customization, layers) || [];
		let result: string = customization.placeholder || applicableLayers?.[0]?.text?.content
		
		// removing html elements from placeholder:
		if (result) {
			const parser = new DOMParser();
			const doc = parser.parseFromString(result, 'text/html');
			result = doc.body.innerText;
		}

		return result;
	}

	public static updateLayer(layer: LayerDto, customization: CustomizationTextDto, value: string): LayerDto {
		switch (layer.type) {
			case 'button':
			case 'text':
				return {
					...layer,
					text: {
						...layer.text,
						content: value
					}
				}

			case 'html':
				return {
					...layer,
					html: {
						...layer.html,
						content: value
					}
				}
		}
	}

	public static applyStringValue(customization: CustomizationTextDto, value: string): CustomizationTextDto {
		return {
			...customization,
			value
		};
	}
}

export class CustomizationSliderDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.SLIDER;

	@IsOptional()
	value?: string | string[]; // Can be a single value or a range.

	@IsString()
	@IsOptional()
	minValue?: string;

	@IsString()
	@IsOptional()
	maxValue?: string;

	@IsString()
	@IsOptional()
	template?: 'single' | 'range';

	@IsString()
	@IsOptional()
	variableKey?: string;

	@IsString()
	@IsOptional()
	variableRangeKey?: string;

	public static getDefaultValue(customization: CustomizationSliderDto, layers?: LayerDto[], mergeTagState?: { [key: string]: any }) {
		return customization.value ?? customization.defaultValue ?? customization.minValue ?? customization.maxValue ?? undefined;
	}

	public static updateLayer(layer: LayerDto, customization: CustomizationTextDto, value: string): LayerDto {
		// Don't affect layers with range customizations.
		return layer;
	}
}

export class ImagePickerActionsTargetCropConfigDto {
	maintainAspectRatio: boolean;
	elementSelector: string; // Used to find an element in the unit to get dimensions from.
	aspectRatio: number | string;
	target: {
		x: number;
		y: number;
		width: number;
		height: number;
		cropMode?: 'cover' | 'contain';
		aspectPriority?: 'crop' | 'target'; // How to prioritize aspect ratio vs. target area (default: 'target')
	};
	imageHeight: number;
	imageWidth: number;
}

export class ImagePickerActionsDto {
	@IsOptional()
	enabled?: boolean;

	@IsOptional()
	actionsButtonLabel?: string;

	@IsOptional()
	actionsButtonIcon?: string;

	@IsOptional()
	@IsArray()
	showActions?: ImageActionType[];

	@IsOptional()
	@IsArray()
	hideActions?: ImageActionType[];

	@IsOptional()
	@IsBoolean()
	minimizeSidebar: boolean;

	@IsOptional()
	@IsBoolean()
	hideSidebar: boolean;

	@IsOptional()
	targetCropConfig: ImagePickerActionsTargetCropConfigDto;

	@IsOptional()
	dataSource?: PublicDataSource;

	@IsOptional()
	disableSavingOnModify?: boolean;

}

export interface AssetCardLayout {
	id: string;
	name: string;
	width: number;
	height: number;
}

export class AssetPickerConfigDto {
	datasource: PublicDataSource;

	/**
	 * Optional array of datasources for multi-datasource mode.
	 * When provided, the asset picker dialog will display a sidebar
	 * allowing users to navigate between datasources.
	 * Use this instead of a single `datasource` when users need access
	 * to multiple datasources within the same dialog session.
	 */
	@IsOptional()
	datasources?: PublicDataSource[];

	@IsOptional()
	mediaType?: 'image' | 'video' | 'audio' | string;

	@IsOptional()
	@Type(() => QueryParams)
	queryParams?: QueryParams;

	@IsOptional()
	selectedItems?: AssetDto[];

	@IsOptional()
	selectMultiple?: boolean;

	@IsOptional()
	ui?: {
		layout?: AssetCardLayout;
		layoutOptions?: AssetCardLayout[];
		hideToolbar?: boolean;
		hideSearch?: boolean;
		showUpload?: boolean;
	};
}

export class CustomizationMediaPickerDto extends CustomizationDto {
	@IsOptional()
	showSelectButton?: boolean;

	@IsOptional()
	selectButtonLabel?: string;

	@IsOptional()
	selectButtonIcon?: string;

	@IsOptional()
	showResetButton?: boolean;

	@IsOptional()
	resetButtonLabel?: string;

	@IsOptional()
	resetButtonIcon?: string;

	@IsOptional()
	showPreview?: boolean;
}

export class CustomizationImagePickerDto extends CustomizationMediaPickerDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.IMAGE_PICKER;

	@IsOptional()
	value?: AssetDto;

	@IsOptional()
	actions?: ImagePickerActionsDto;

	@IsOptional()
	dataSources?: (PublicDataSource['slug'] | PublicDataSource['id'])[];

	@IsOptional()
	@IsBoolean()
	useUnitAssetPath?: boolean; // TODO: Move to Media Picker and implement for video and audio.

	@IsOptional()
	@IsBoolean()
	hideMediaTitle?: boolean;

	/**
	 * If there isn't a current value, pull in the image asset from the first layer that matches the customization.
	 */
	public static getDefaultValue(customization: CustomizationImagePickerDto, layers?: LayerDto[]) {

		// console.log('Applicable Layers', applicableLayers, customization.label, applicableLayers?.[0]?.image);
		if (customization.value) {
			return customization.value;
		}

		const applicableLayers = CreativeUnitUtils.getLayersFromCustomization(customization, layers) || [];
		if (applicableLayers?.[0]?.assetPath) {
			// console.log('Using Asset Path', customization.label, applicableLayers?.[0]?.assetPath, customization);

			// UPDATE: We don't HAVE to fill in a value, since our picker can still process a preview without one and it makes it
			// easier to understand whether the user has modified the picker.
			// Left previous logic commented for now.
			return;

			// let layer = applicableLayers?.[0];
			// if (customization.mappings && customization.value) {
			// 	console.log('Applying Mappings', customization.label, customization.mappings, customization.value);
			// 	layer = this.applyMappingsToLayers(layer, customization.mappings, customization.value);
			// 	console.log('Layer with Mappings', layer);
			// }

			// return {
			// 	assetPath: layer?.assetPath,
			// 	mappings: JSON.parse(
			// 		MergeTags.applyMergeTagsToString(JSON.stringify(customization.mappings || {}), applicableLayers?.[0]?.assetPath)
			// 	)
			// };
		}
		if (applicableLayers?.[0]?.image) {
			return {
				...applicableLayers?.[0]?.image,
				mappings: JSON.parse(
					// TODO: Scripts - Need package data for script support
					MergeTags.applyMergeTagsToString(JSON.stringify(customization.mappings || {}), applicableLayers?.[0]?.image)
				)
			};
		}
	}

	public static updateLayer(
		layer: LayerDto,
		customization: CustomizationImagePickerDto,
		value: AssetDto & { mappings?: CustomizationOptionMappingDto },
		creativeUnit: CreativeUnitDto
	): LayerDto {
		let newLayer = {
			...this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit),
			image: value,
			// Update the asset path too just to ensure it is set.
			assetPath: value?.assetPath || layer?.assetPath,
		};

		if (customization.layerIds.includes(layer.id)) {
			if (value?.target) {
				let target = { ...value.target };
				if (String.isEmpty(target?.width)) {
					target.width = newLayer.optimizations?.targetCrop?.tw;
				}

				if (String.isEmpty(target?.height)) {
					target.height = newLayer.optimizations?.targetCrop?.th;
				}

				if (String.isEmpty(target?.x)) {
					target.x = newLayer.optimizations?.targetCrop?.tx;
				}

				if (String.isEmpty(target?.y)) {
					target.y = newLayer.optimizations?.targetCrop?.ty;
				}

				newLayer = {
					...newLayer,
					optimizations: {
						...newLayer.optimizations,
						targetCrop: {
							...newLayer.optimizations?.targetCrop,
							th: target.height,
							tw: target.width,
							tx: target.x,
							ty: target.y
						}
					}
				};
			}
		}

		return newLayer;
	}

	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationImagePickerDto,
		value: AssetDto & { mappings?: CustomizationOptionMappingDto },
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		return LayersUtils.mapAllLayers(layers, layer => {
			let newLayer = this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit);

			// Check for crop data within the image picker value.
			if (value?.target && customization.layerIds.includes(layer.id)) {
				let target = { ...value.target };
				if (String.isEmpty(target?.width)) {
					target.width = newLayer.optimizations?.targetCrop?.tw;
				}

				if (String.isEmpty(target?.height)) {
					target.height = newLayer.optimizations?.targetCrop?.th;
				}

				if (String.isEmpty(target?.x)) {
					target.x = newLayer.optimizations?.targetCrop?.tx;
				}

				if (String.isEmpty(target?.y)) {
					target.y = newLayer.optimizations?.targetCrop?.ty;
				}

				newLayer = {
					...newLayer,
					optimizations: {
						...newLayer.optimizations,
						targetCrop: {
							...newLayer.optimizations?.targetCrop,
							th: target.height,
							tw: target.width,
							tx: target.x,
							ty: target.y
						}
					}
				};
			}

			if (customization.layerIds.includes(layer.id)) {
				return {
					...newLayer,
					image: value
				};
			} else {
				return newLayer;
			}
		});
	}

	public static applyStringValue(customization: CustomizationImagePickerDto, value: string): CustomizationImagePickerDto {
		return {
			...customization,
			value: {
				...customization.value,
				assetPath: value
			}
		};
	}
}

export class AudioPickerActionsDto{
	@IsOptional()
	enabled?: boolean;

	@IsOptional()
	actionsButtonLabel?: string;

	@IsOptional()
	actionsButtonIcon?: string;

	@IsOptional()
	@IsArray()
	showActions?: AudioActionType[];

	@IsOptional()
	@IsArray()
	hideActions?: AudioActionType
}

export class VideoPickerActionsDto {
	@IsOptional()
	enabled?: boolean;

	@IsOptional()
	actionsButtonLabel?: string;

	@IsOptional()
	actionsButtonIcon?: string;

	@IsOptional()
	@IsArray()
	showActions?: VideoActionType[];

	@IsOptional()
	@IsArray()
	hideActions?: VideoActionType[];

	@IsOptional()
	@IsBoolean()
	minimizeSidebar: boolean;

	@IsOptional()
	@IsBoolean()
	hideSidebar: boolean;

	@IsOptional()
	targetCropConfig: ImagePickerActionsTargetCropConfigDto;

	@IsOptional()
	dataSource?: PublicDataSource;

	@IsOptional()
	disableSavingOnModify?: boolean;
}

export class CustomizationVideoPickerDto extends CustomizationMediaPickerDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.VIDEO_PICKER;

	@IsOptional()
	value?: LayerVideoDto | (AssetDto & { file: LayerVideoDto; audioConfig: LayerAudioDto });

	@IsOptional()
	dataSources?: (PublicDataSource['slug'] | PublicDataSource['id'])[];

	@IsOptional()
	actions?: VideoPickerActionsDto;

	@IsOptional()
	@IsBoolean()
	useUnitAssetPath?: boolean; // TODO: Move to Media Picker and implement for video and audio.

	/**
	 * If there isn't a current value, pull in the video asset from the first layer that matches the customization.
	 */
	public static getDefaultValue(customization: CustomizationItemDto, layers?: LayerDto[]) {
		let val = customization.value;
		if (!customization.value) {
			const applicableLayers = CreativeUnitUtils.getLayersFromCustomization(customization, layers) || [];
			if (applicableLayers?.length) {
				val = {
					file: applicableLayers[0].video,
					audioConfig: applicableLayers[0].audioConfig,
					mappings: JSON.parse(
						MergeTags.applyMergeTagsToString(JSON.stringify(customization.mappings || {}), applicableLayers?.[0]?.video)
					)
				};
			}
		}

		return val;
	}

	public static updateLayer(
		layer: LayerDto,
		customization: CustomizationItemDto,
		value: { file: AssetDto; audioConfig: LayerAudioDto },
		creativeUnit: CreativeUnitDto
	): LayerDto {
		if (value.file) {
			return {
				...this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit),
				video: merge(cloneDeep(layer.video), value.file),
				audioConfig: merge(cloneDeep(layer.audioConfig), value.audioConfig)
			};
		} else {
			// Backwards compatibility.
			return {
				...this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit),
				video: merge(cloneDeep(layer.video), value)
			};
		}
	}

	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationItemDto,
		value: { file: AssetDto; audioConfig: LayerAudioDto },
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		return LayersUtils.mapAllLayers(layers, layer => {
			let newLayer = this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit);

			if (layer.id === customization.layerIds?.[0]) {
				if (value.file) {
					return {
						...newLayer,
						video: merge(cloneDeep(newLayer.video), value.file),
						audioConfig: merge(cloneDeep(newLayer.audioConfig), value.audioConfig)
					};
				} else {
					// Backwards compatibility
					return {
						...newLayer,
						video: merge(cloneDeep(newLayer.video), value)
					};
				}
			} else {
				return newLayer;
			}
		});
	}

	public static applyStringValue(customization: CustomizationVideoPickerDto, value: string): CustomizationVideoPickerDto {
		return {
			...customization,
			value: {
				...customization.value,
				assetPath: value
			}
		};
	}
}

export class CustomizationAudioPickerDto extends CustomizationMediaPickerDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.AUDIO_PICKER;

	@IsOptional()
	value?: { file: AssetDto; audioConfig: LayerAudioDto };

	@IsOptional()
	dataSources?: (PublicDataSource['slug'] | PublicDataSource['id'])[];

	@IsOptional()
	actions?: AudioPickerActionsDto;

	/**
	 * If there isn't a current value, pull in the audio asset from the first layer that matches the customization.
	 */
	public static getDefaultValue(customization: CustomizationItemDto, layers?: LayerDto[]) {
		let val = customization.value;
		if (!customization.value) {
			const applicableLayers = CreativeUnitUtils.getLayersFromCustomization(customization, layers) || [];
			if (applicableLayers?.length) {
				val = {
					file: applicableLayers[0].audio,
					audioConfig: applicableLayers[0].audioConfig,
					mappings: JSON.parse(
						MergeTags.applyMergeTagsToString(JSON.stringify(customization.mappings || {}), applicableLayers?.[0]?.audio)
					)
				};
			}
		}

		return val;
	}

	public static updateLayer(
		layer: LayerDto,
		customization: CustomizationItemDto,
		value: { file: AssetDto; audioConfig: LayerAudioDto },
		creativeUnit: CreativeUnitDto
	): LayerDto {
		if (value.file) {
			return {
				...this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit),
				audio: merge(cloneDeep(layer.audio), value.file),
				audioConfig: merge(cloneDeep(layer.audioConfig), value.audioConfig)
			};
		} else {
			// Backwards compatibility.
			return {
				...this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit),
				audio: merge(cloneDeep(layer.audio), value)
			};
		}
	}

	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationItemDto,
		value: { file: AssetDto; audioConfig: LayerAudioDto },
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		return LayersUtils.mapAllLayers(layers, layer => {
			let newLayer = this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit);

			if (layer.id === customization.layerIds?.[0]) {
				if (value.file) {
					return {
						...newLayer,
						audio: merge(cloneDeep(layer.audio), value.file),
						audioConfig: merge(cloneDeep(layer.audioConfig), value.audioConfig)
					};
				} else {
					// Backwards compatibility.
					return {
						...newLayer,
						audio: merge(cloneDeep(layer.audio), value)
					};
				}
			} else {
				return newLayer;
			}
		});
	}

	public static applyStringValue(customization: CustomizationAudioPickerDto, value: string): CustomizationAudioPickerDto {
		return {
			...customization,
			value: {
				...customization.value,
				file: {
					...customization.value.file,
					assetPath: value
				}
			}
		};
	}
}

export class DefaultValueVariableDto {
	@IsString()
	@IsNotEmpty()
	itemKey: string;

	@IsString()
	@IsNotEmpty()
	variablePath: string;
}

export class CustomizationSelectDynamicLinkDto {
	@IsOptional()
	@IsString()
	dataSourceSlug: string;

	@IsOptional()
	@IsString()
	labelKey: string;

	@IsOptional()
	@ValidateNested()
	@Type(() => QueryParams)
	params?: QueryParams;

	@IsOptional()
	@ValidateNested()
	@Type(() => CustomizationOptionMappingDto)
	mapping?: CustomizationOptionMappingDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => DefaultValueVariableDto)
	defaultValueVariable?: DefaultValueVariableDto;
}

export class CustomizationSelectDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.SELECT;

	@ValidateNested()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	value?: CustomizationOptionDto;

	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	options: CustomizationOptionDto[];

	@ValidateNested()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	defaultOption?: CustomizationOptionDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => CustomizationSelectDynamicLinkDto)
	dynamicLink?: CustomizationSelectDynamicLinkDto;

	private static checkDefaultValueVariable(
		customization: CustomizationSelectDto,
		availableOptions: CustomizationOptionDto[],
		mergeTagState: { [key: string]: any },
	) {
		if (customization?.dynamicLink?.defaultValueVariable && availableOptions?.length && !customization.dirty) {
			// If `defaultValueVariable` is set, attempt to find the default value based on the variable
			// and the itemKey in `mergeTagState.variables` and `newOptions`.
			// If this is configured improperly, it will try to find options by `label` key.
			// If nothing is found, set the first option.
			const varValue = ObjectUtils.resolveDotNotationPath(customization?.dynamicLink?.defaultValueVariable?.variablePath, mergeTagState.variables);

			if (varValue) {
				if (!customization.dynamicLink.defaultValueVariable.itemKey) {
					customization.dynamicLink.defaultValueVariable.itemKey = 'label';
				}

				const defaultOption = availableOptions.find(o => {
					const resolvedValue = ObjectUtils.resolveDotNotationPath(customization?.dynamicLink?.defaultValueVariable?.itemKey, o);
					const isMatch = resolvedValue === varValue;
					return isMatch;
				});

				if (defaultOption) {
					// console.log('Found default value variable match for', customization.label, defaultOption);
					return defaultOption;
				}
			}
		}

		// console.log('No default value variable match found for', customization.label);
		return null;
	}

	/**
	 * If the value doesn't exist, pull in the default option from the first layer that matches the customization.
	 * If that doesn't exist, pull in the first option from the list.
	 */
	public static getDefaultValue(
		customization: CustomizationSelectDto,
		layers?: LayerDto[],
		mergeTagState?: { [key: string]: any },
		dynamicOptions?: CustomizationOptionDto[]
	) {

		// console.group('Get Default Value', customization.label, customization.name, customization, mergeTagState);
		// if (!mergeTagState) {
		// 	console.log('No merge tag state, returning null');
		// }

		let values = [customization.value, customization.defaultValue, customization.defaultOption];
		let dirty = customization.dirty;


		let defaultVarResult: CustomizationOptionDto;

		if (customization.dynamicLink && dynamicOptions?.length) {
			// Check if we have a default value variable that should be taking priority over the rest
			defaultVarResult = this.checkDefaultValueVariable(customization, dynamicOptions, mergeTagState);
			if (defaultVarResult) {
				// console.groupEnd();
				return { newValue: defaultVarResult, dirty: dirty };
			}
		}

		// Find and validate the value by ensuring it exists within the available options
		for (const [index, value] of values.entries()) {
			if (!value) {
				continue;
			}
			if (index === 0 && !dirty) {
				// If the customization is not dirty, we don't need to check the current value, prefer falling back instead.
				continue;
			}
			if (index > 0) {
				dirty = false; // defaultValue and defaultOption are not dirty
			}

			const matchedValue = this.findMatchedValue(customization, value, dynamicOptions);
			const options: CustomizationOptionDto[] = matchedValue ? [matchedValue] : [];

			if (index === 0 && dirty) {
				// console.log('Current value is dirty, adding non-strict matches', value);
				// If the current value is dirty but not found, try searching non-strictly (e.g., in case we are expected to switch between similar options)
				const possibleOptions: CustomizationOptionDto[] = this.findMatchedValues(customization, value, dynamicOptions).filter(o => {
					// Exclude the exact match since we already checked that
					const isExactMatch = CustomizationOptionUtils.areOptionsEqual(o, value, true);
					return !isExactMatch;
				});

				// console.log('Adding possible options for dirty value', possibleOptions);
				if (possibleOptions?.length) {
					options.push(...possibleOptions);
				}
			}

			if (!options?.length) {
				continue;
			}

			// console.log('Checking options', options);
			for (const option of options) {
				// console.log('Checking value', { value, option });
				if (option?.visibilityConditions?.length) {
					// console.log('Has visibility conditions', option.visibilityConditions, mergeTagState)
					const valid = Evaluate.evaluateMultiple(option.visibilityConditions, mergeTagState);
					if (valid) {
						// console.log(customization.label, `Found a valid value with visibility conditions ${index}`, { value, dirty });
						// console.groupEnd();
						return { newValue: option, dirty: dirty };
					}
				} else {
					// We're good!
					// console.log(customization.label, `Found a valid value ${index}`, { value, dirty });
					// console.groupEnd();
					return { newValue: option, dirty: dirty };
				}
				// console.log('Value not valid due to visibility conditions', { value, option });
			}
		}

		// The value is not valid, dirty state has been reset.
		dirty = false;

		// The old value is not valid anymore, so let's recheck the defaultValueVariable, and if that doesn't exist just use the first available option.
		if (customization.dynamicLink && dynamicOptions?.length) {
			defaultVarResult = this.checkDefaultValueVariable(customization, dynamicOptions, mergeTagState);
			if (defaultVarResult) {
				// console.groupEnd();
				return { newValue: defaultVarResult, dirty: dirty };
			}
		}

		// we have not found anything interesting, use the first available option whether it's dynamic or static
		if (customization.dynamicLink && dynamicOptions?.length) {
			// console.log('Found a default option', dynamicOptions[0]);
			// console.groupEnd();
			return { newValue: dynamicOptions[0], dirty: dirty };
		}

		const availableOptions: CustomizationOptionDto[] = CustomizationOptionUtils.getVisibleOptions(customization.options, mergeTagState);

		if (availableOptions?.length) {
			// console.log('Found a default option', availableOptions[0]);
			// console.groupEnd();
			return { newValue: availableOptions[0], dirty: dirty };
		}

		// console.log('No default option found, returning null');
		// console.groupEnd();
		return { newValue: null, dirty: dirty };
	}

	/**
	 * Find a single option that matches the selected option.
	 * @param customization
	 * @param selectedOption
	 * @param dynamicOptions
	 */
	public static findMatchedValue(
		customization: CustomizationSelectDto,
		selectedOption: CustomizationOptionDto,
		dynamicOptions?: CustomizationOptionDto[],
	): any {
		// For dynamic customizations with options available, ONLY match against dynamic options.
		// This ensures that stale values from a different upstream selection are not considered valid.
		if (customization.dynamicLink && dynamicOptions?.length) {
			const dynamicOption = dynamicOptions.find(o => {
				const isEqual = CustomizationOptionUtils.areOptionsEqual(o, selectedOption);
				return isEqual;
			});
			return dynamicOption; // Returns undefined if not found, which is correct behavior
		}

		// For non-dynamic customizations, check static options
		const option = customization.options?.find(o => {
			const isEqual = CustomizationOptionUtils.areOptionsEqual(o, selectedOption);
			return isEqual;
		});

		return option;
	}

	/**
	 * Find all options that match the selected option.
	 * @param customization
	 * @param selectedOption
	 * @param dynamicOptions
	 * @param strict Whether to use strict equality or not. Defaults to false.
	 */
	public static findMatchedValues(
		customization: CustomizationSelectDto,
		selectedOption: CustomizationOptionDto,
		dynamicOptions?: CustomizationOptionDto[],
		strict: boolean = false
	): CustomizationOptionDto[] {
		// For dynamic customizations with options available, ONLY match against dynamic options.
		// This ensures that stale values from a different upstream selection are not considered valid.
		if (customization.dynamicLink && dynamicOptions?.length) {
			return dynamicOptions.filter(o => {
				return CustomizationOptionUtils.areOptionsEqual(o, selectedOption, strict);
			});
		}

		// For non-dynamic customizations, check static options
		return customization.options?.filter(o => {
			return CustomizationOptionUtils.areOptionsEqual(o, selectedOption, strict);
		}) || [];
	}

	public static updateLayer(
		layer: LayerDto,
		customization: CustomizationItemDto,
		value: CustomizationOptionDto,
		creativeUnit: CreativeUnitDto
	): LayerDto {
		return CustomizationUtils.applyCustomizationValueToLayer(value, layer, creativeUnit);
	}

	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationItemDto,
		value: CustomizationOptionDto,
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		// console.log('Update Select Layers', creativeUnit.name, customization.label, layers, customization, value);
		return LayersUtils.mapAllLayers(layers, layer => {
			return CustomizationUtils.applyCustomizationValueToLayer(value, layer, creativeUnit);
		});
	}

	public static applyStringValue(customization: CustomizationSelectDto, value: string): CustomizationSelectDto {
		const matches = value.match(/\((.*?)\)/);
		const id = matches ? matches[matches.length - 1] : null;

		const option = customization.options.find((item: CustomizationOptionDto) => item.id === id);

		if (!option) {
			return customization;
		}

		return {
			...customization,
			value: option
		};
	}
}

/**
 * Toggle Customization
 * A toggle customization is a select customization that only has two options.
 * It basically works the same as a select, but there is only a true option and a false option.
 */
export class CustomizationToggleDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.TOGGLE;

	@ValidateNested()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	value?: CustomizationOptionDto;

	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	options: CustomizationOptionDto[];

	/**
	 * If the value doesn't exist, pull in the default option from the first layer that matches the customization.
	 * If that doesn't exist, pull in the first option from the list.
	 */
	public static getDefaultValue(customization: CustomizationSelectDto, layers?: LayerDto[]) {
		const selectedOption = customization.value || customization.defaultValue || customization.defaultOption;
		return this.findMatchedValue(customization, selectedOption) || customization.options?.[0];
	}

	public static findMatchedValue(customization: CustomizationSelectDto, selectedOption: CustomizationOptionDto): any {
		// console.log('Find Matched Value', customization, selectedOption);
		if (!selectedOption) {
			return null;
		}
		const option = customization.options?.find(o => o.value === selectedOption.value);
		return option;
	}

	public static updateLayer(
		layer: LayerDto,
		customization: CustomizationItemDto,
		value: CustomizationOptionDto,
		creativeUnit: CreativeUnitDto
	): LayerDto {
		return CustomizationUtils.applyCustomizationValueToLayer(value, layer, creativeUnit);
	}

	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationItemDto,
		value: CustomizationOptionDto,
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		// console.log('Update Layers', creativeUnit.name, layers, customization, value);
		return LayersUtils.mapAllLayers(layers, layer => {
			return CustomizationUtils.applyCustomizationValueToLayer(value, layer, creativeUnit);
		});
	}

	public static applyStringValue(customization: CustomizationSelectDto, value: string): CustomizationSelectDto {
		const matches = value.match(/\((.*?)\)/);
		const id = matches ? matches[matches.length - 1] : null;

		const option = customization.options.find((item: CustomizationOptionDto) => item.id === id);

		if (!option) {
			return customization;
		}

		return {
			...customization,
			value: option
		};
	}
}

export class CustomizationDimensionsDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.DIMENSIONS;

	@IsString()
	@IsOptional()
	value?: CreativeUnitDimensionsDto;

	@IsBoolean()
	@IsOptional()
	resetSizes?: boolean;

	@Matches(Validation.numberOrPercentRgx, { message: 'Aspect Max must be a number or percent.' })
	@IsOptional()
	aspectMin?: string;

	@Matches(Validation.numberOrPercentRgx, { message: 'Aspect Max must be a number or percent.' })
	@IsOptional()
	aspectMax?: string;

	@IsArray()
	@IsOptional()
	@IsString({ each: true })
	canTransformInto?: string[]; // Other units this one can transform into.

	public static getDefaultValue(customization: CustomizationTextDto, layers?: LayerDto[]) {
		// Use the value if it exists, otherwise, don't pull a default.
		// This is so that the fields will be blank and the placeholder will fill in.
		return customization.value || undefined;
	}

	public static updateLayer(layer: LayerDto, customization: CustomizationTextDto, value: string): LayerDto {
		return;
	}
}

export class CustomizationColorPickerDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.COLOR_PICKER;

	@ValidateNested()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	value?: CustomizationOptionDto;

	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	options: CustomizationOptionDto[];

	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	userColors: CustomizationOptionDto[];

	@ValidateNested()
	@IsOptional()
	@Type(() => CustomizationOptionDto)
	defaultOption?: CustomizationOptionDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => CustomizationSelectDynamicLinkDto)
	dynamicLink?: CustomizationSelectDynamicLinkDto;

	@IsOptional()
	@IsBoolean()
	allowUserColors?: boolean;

	@IsOptional()
	@IsBoolean()
	allowCustomColor?: boolean;

	/**
	 * If the value doesn't exist, pull in the default option from the first layer that matches the customization.
	 * If that doesn't exist, pull in the first option from the list.
	 */
	public static getDefaultValue(customization: CustomizationSelectDto, layers?: LayerDto[], mergeTagState?: { [key: string]: any }) {
		// console.group('Get Default Value', customization.label, customization.name, customization, mergeTagState);
		let values = [customization.value, customization.defaultValue, customization.defaultOption];
		// Validate the value to ensure it exists in the options list.
		for (let value of values) {
			if (!value) {
				continue;
			}
			// console.log('Checking value', value);
			if (value?.visibilityConditions?.length) {
				let valid = Evaluate.evaluateMultiple(value.visibilityConditions, mergeTagState);
				if (valid) {
					// console.log('Found a valid default with visibility conditions', value);
					// console.groupEnd();
					return value;
				}
			} else {
				// We're good!
				// console.log('Found a valid default', value);
				// console.groupEnd();
				return value;
			}
		}

		// console.log('Nothing is valid, let\'s find a default option', customization.options);

		// If we get here, let's just iterate through options till we find SOMETHING to use.
		for (let option of customization.options || []) {
			if (option?.visibilityConditions?.length) {
				let valid = Evaluate.evaluateMultiple(option.visibilityConditions, mergeTagState);
				if (valid) {
					// console.log('Found a default option with visbility conditions', option);
					// console.groupEnd();
					return option;
				}
			} else {
				// We're good!
				// console.log('Found a default option', option);
				// console.groupEnd();
				return option;
			}
		}
	}

	public static findMatchedValue(customization: CustomizationSelectDto, selectedOption: CustomizationOptionDto): any {
		// console.log('Find Matched Value', customization, selectedOption);
		return customization.options?.find(o => o.id === selectedOption.id);
	}

	public static updateLayer(
		layer: LayerDto,
		customization: CustomizationColorPickerDto,
		value: CustomizationOptionDto & { mappings?: CustomizationOptionMappingDto },
		creativeUnit: CreativeUnitDto
	): LayerDto {
		// console.log('Update Layer', layer, customization, value);

		return {
			...this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit),
			style: value
		};
	}

	public static updateLayers(
		layers: LayerDto[],
		customization: CustomizationColorPickerDto,
		value: CustomizationOptionDto & { mappings?: CustomizationOptionMappingDto },
		creativeUnit: CreativeUnitDto
	): LayerDto[] {
		// console.log('Update Layers', creativeUnit.name, layers, customization, value);
		return LayersUtils.mapAllLayers(layers, layer => {
			let newLayer = this.applyMappingsToLayers(layer, customization?.mappings, value, creativeUnit);

			if (layer.id === customization.layerIds?.[0]) {
				return {
					...newLayer,
					style: value
				};
			} else {
				return newLayer;
			}
		});
	}

	public static applyStringValue(customization: CustomizationSelectDto, value: string): CustomizationSelectDto {
		const matches = value.match(/\((.*?)\)/);
		const id = matches ? matches[matches.length - 1] : null;

		const option = customization.options.find((item: CustomizationOptionDto) => item.id === id);

		if (!option) {
			return customization;
		}

		return {
			...customization,
			value: option
		};
	}
}

export class CustomizationMessageDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.MESSAGE;

	@IsString()
	@IsOptional()
	value?: string;

	@IsString()
	@IsOptional()
	template?: 'wysiwyg' | 'text' | 'textarea';

	public static applyStringValue(customization: CustomizationTextDto, value: string): CustomizationTextDto {
		return {
			...customization,
			value
		};
	}
}

export class CustomizationGroupDto extends CustomizationDto {
	@IsEnum(CustomizationType)
	type: CustomizationType.GROUP;

	@IsOptional()
	@IsString()
	value?: string = null;

	@ValidateNested({ each: true })
	@IsArray()
	@IsOptional()
	@Type(() => CustomizationDto, {
		keepDiscriminatorProperty: true,
		discriminator: {
			property: 'type',
			subTypes: [
				// TODO: Find a way to use the type array below here.
				{ value: CustomizationTextDto, name: CustomizationType.TEXT },
				{ value: CustomizationSelectDto, name: CustomizationType.SELECT },
				{ value: CustomizationToggleDto, name: CustomizationType.TOGGLE },
				{ value: CustomizationSliderDto, name: CustomizationType.SLIDER },
				{ value: CustomizationImagePickerDto, name: CustomizationType.IMAGE_PICKER },
				{ value: CustomizationVideoPickerDto, name: CustomizationType.VIDEO_PICKER },
				{ value: CustomizationAudioPickerDto, name: CustomizationType.AUDIO_PICKER },
				{ value: CustomizationColorPickerDto, name: CustomizationType.COLOR_PICKER },
				{ value: CustomizationDimensionsDto, name: CustomizationType.DIMENSIONS },
				{ value: CustomizationMessageDto, name: CustomizationType.MESSAGE },
				{ value: CustomizationGroupDto, name: CustomizationType.GROUP }
			]
		}
	})
	children?: CustomizationItemDto[];

	@IsOptional()
	@IsBoolean()
	collapsedByDefault?: boolean;
}

export const CustomizationTypeArray = [
	{ value: CustomizationTextDto, name: CustomizationType.TEXT },
	{ value: CustomizationSelectDto, name: CustomizationType.SELECT },
	{ value: CustomizationToggleDto, name: CustomizationType.TOGGLE },
	{ value: CustomizationSliderDto, name: CustomizationType.SLIDER },
	{ value: CustomizationImagePickerDto, name: CustomizationType.IMAGE_PICKER },
	{ value: CustomizationVideoPickerDto, name: CustomizationType.VIDEO_PICKER },
	{ value: CustomizationAudioPickerDto, name: CustomizationType.AUDIO_PICKER },
	{ value: CustomizationColorPickerDto, name: CustomizationType.COLOR_PICKER },
	{ value: CustomizationDimensionsDto, name: CustomizationType.DIMENSIONS },
	{ value: CustomizationMessageDto, name: CustomizationType.MESSAGE },
	{ value: CustomizationGroupDto, name: CustomizationType.GROUP }
];

